import { useState, useEffect, useMemo, useCallback } from "react";
import { authHeaders } from "../supabase";

const API = process.env.REACT_APP_API_URL || "https://api.mioprice.com";

const PAYER_LABELS = {
  uhc:    "UHC",
  aetna:  "Aetna",
  cigna:  "Cigna",
  kaiser: "Kaiser",
  bcbs:   "BCBS",
};

const PAYER_COLORS = {
  uhc:    { bg: "#dbeafe", text: "#1d4ed8", border: "#bfdbfe" },
  aetna:  { bg: "#fae8ff", text: "#7e22ce", border: "#e9d5ff" },
  cigna:  { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" },
  kaiser: { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  bcbs:   { bg: "#fef3c7", text: "#b45309", border: "#fde68a" },
};

const fmt = (n) => n == null ? "—" : "$" + Math.round(n).toLocaleString();
const fmtRange = (lo, hi) => lo == null ? "—" : lo === hi ? fmt(lo) : `${fmt(lo)}–${fmt(hi)}`;

const isZip = (s) => /^\d{5}$/.test((s || "").trim());

// ── Geocode city ──────────────────────────────────────────────────────────────
const geoCache = {};
async function geocodeCity(input) {
  const key = input.toLowerCase().trim();
  if (geoCache[key]) return geoCache[key];
  try {
    const q = encodeURIComponent(key + (key.includes(",") ? "" : ", USA"));
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=3&q=${q}&countrycodes=us&addressdetails=1`,
      { headers: { "User-Agent": "MioPrice/1.0" } }
    );
    if (r.ok) {
      const d = await r.json();
      if (d.length > 0) {
        const best = d.find(x => ["city","town"].includes(x.type)) || d[0];
        const city  = best.address?.city || best.address?.town || (best.display_name || "").split(",")[0];
        const state = best.address?.state_code || "";
        const result = { lat: parseFloat(best.lat), lng: parseFloat(best.lon),
                         label: (city || "").trim() + (state ? ", " + state : "") };
        geoCache[key] = result;
        return result;
      }
    }
  } catch {}
  return null;
}

// ── Market position badge ─────────────────────────────────────────────────────
function PositionBadge({ rate, allRates }) {
  if (rate == null || !allRates.length) return <span style={{ color: "#9ca3af" }}>—</span>;
  const min = Math.min(...allRates);
  const max = Math.max(...allRates);
  const pct = max === min ? 50 : ((rate - min) / (max - min)) * 100;
  const isLow  = pct <= 33;
  const isHigh = pct >= 67;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 999,
      background: isLow ? "#dcfce7" : isHigh ? "#fee2e2" : "#fef9c3",
      color: isLow ? "#15803d" : isHigh ? "#b91c1c" : "#92400e",
    }}>
      {isLow ? "▼ Low" : isHigh ? "▲ High" : "● Mid"}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PayerIntelligence() {
  const [location,     setLocation]     = useState("");
  const [locationLabel,setLocationLabel]= useState("");
  const [cpt,          setCpt]          = useState("");
  const [cptLabel,     setCptLabel]     = useState("");
  const [radius,       setRadius]       = useState(15);
  const [running,      setRunning]      = useState(false);
  const [data,         setData]         = useState(null);
  const [error,        setError]        = useState(null);
  const [searchTerms,  setSearchTerms]  = useState([]);
  const [cityList,     setCityList]     = useState([]);
  const [procSugs,     setProcSugs]     = useState([]);
  const [showProcSug,  setShowProcSug]  = useState(false);
  const [termMap,      setTermMap]      = useState({});
  const [citySugs,     setCitySugs]     = useState([]);
  const [showCitySug,  setShowCitySug]  = useState(false);
  const [filterCat,    setFilterCat]    = useState("all");
  const [sortCol,      setSortCol]      = useState(null);  // {payer, dir}
  const [viewAs,       setViewAs]       = useState("cigna");
  const [selectedNpi,  setSelectedNpi]  = useState(null); // for provider drill-down
  const [providerData, setProviderData] = useState(null);
  const [provLoading,  setProvLoading]  = useState(false); // which payer is "Your Rate"

  // Load search terms + city list
  useEffect(() => {
    fetch(API + "/api/search-terms")
      .then(r => r.json())
      .then(d => {
        if (d.terms) {
          const tc = s => s.replace(/\b\w/g, c => c.toUpperCase());
          const unique = [...new Set(d.terms.map(t => tc(t.term)))].sort();
          setSearchTerms(unique);
          const map = {};
          d.terms.forEach(t => { map[tc(t.term)] = t.cpt; map[t.term.toLowerCase()] = t.cpt; });
          setTermMap(map);
        }
      })
      .catch(() => {});
    fetch(API + "/api/cities")
      .then(r => r.json())
      .then(d => { if (d.cities) setCityList(d.cities); })
      .catch(() => {});
  }, []);

  // Derived: active payers from data
  const payers = data?.payers || [];

  // Filtered + sorted results
  const results = useMemo(() => {
    if (!data?.results) return [];
    let rows = data.results;
    if (filterCat !== "all") {
      rows = rows.filter(r => {
        const cat = (r.provider_category || "").toLowerCase();
        if (filterCat === "hospital") return r.is_hospital || cat.includes("facilit") || cat.includes("hospital");
        if (filterCat === "individual") return !r.is_hospital && cat.includes("individual");
        if (filterCat === "ancillary") return cat.includes("ancillary");
        if (filterCat === "behavioral") return cat.includes("behavioral");
        return true;
      });
    }
    if (sortCol) {
      rows = [...rows].sort((a, b) => {
        const aRates = a.rates[sortCol.payer] || [];
        const bRates = b.rates[sortCol.payer] || [];
        const aMin = aRates.length ? Math.min(...aRates.map(r => r.min)) : Infinity;
        const bMin = bRates.length ? Math.min(...bRates.map(r => r.min)) : Infinity;
        return sortCol.dir === "asc" ? aMin - bMin : bMin - aMin;
      });
    }
    return rows;
  }, [data, filterCat, sortCol]);

  // All min rates per payer (for position badges)
  const payerMinRates = useMemo(() => {
    if (!data?.results) return {};
    const out = {};
    for (const payer of payers) {
      out[payer] = data.results
        .flatMap(r => (r.rates[payer] || []).map(x => x.min))
        .filter(Boolean);
    }
    return out;
  }, [data, payers]);

  const runSearch = useCallback(async () => {
    const loc = location.trim();
    const c   = cpt.trim();
    if (!loc || !c) return;
    setRunning(true); setData(null); setError(null);

    try {
      let params = new URLSearchParams({ cpt: c, radius });
      if (isZip(loc)) {
        params.set("zip", loc);
      } else {
        const geo = await geocodeCity(loc);
        if (!geo) { setError(`Could not find "${loc}".`); setRunning(false); return; }
        setLocationLabel(geo.label);
        params.set("lat", geo.lat);
        params.set("lng", geo.lng);
      }
      const r   = await fetch(API + "/api/payer-intelligence?" + params, { headers: await authHeaders() });
      const d   = await r.json();
      if (!r.ok) { setError(d.error || "Request failed"); setRunning(false); return; }
      setData(d);
    } catch (e) {
      setError(e.message);
    }
    setRunning(false);
  }, [location, cpt, radius]);

  const fetchProviderIntel = async (npi, state) => {
    if (selectedNpi === npi) { setSelectedNpi(null); setProviderData(null); return; }
    setSelectedNpi(npi); setProvLoading(true); setProviderData(null);
    try {
      const p = new URLSearchParams({ npi, cpt, radius, state });
      const r = await fetch(API + "/api/provider-intelligence?" + p, { headers: await authHeaders() });
      const d = await r.json();
      if (r.ok) setProviderData(d);
    } catch(e) { console.error(e); }
    setProvLoading(false);
  };

  const toggleSort = (payer) => {
    setSortCol(prev =>
      prev?.payer === payer
        ? prev.dir === "asc" ? { payer, dir: "desc" } : null
        : { payer, dir: "asc" }
    );
  };

  // Category counts
  const catCounts = useMemo(() => {
    if (!data?.results) return {};
    const out = { all: data.results.length, hospital: 0, individual: 0, ancillary: 0, behavioral: 0 };
    for (const r of data.results) {
      const cat = (r.provider_category || "").toLowerCase();
      if (r.is_hospital || cat.includes("facilit") || cat.includes("hospital")) out.hospital++;
      else if (cat.includes("individual")) out.individual++;
      else if (cat.includes("ancillary")) out.ancillary++;
      else if (cat.includes("behavioral")) out.behavioral++;
    }
    return out;
  }, [data]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 16px", fontFamily: "inherit" }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: "#111827" }}>Payer Intelligence</span>
          <span style={{ fontSize: 11, fontWeight: 600, background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: 999 }}>BETA</span>
        </div>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
          Cross-payer rate benchmarking from federal Transparency in Coverage data. Compare negotiated rates at any provider across UHC, Aetna, Cigna and more.
        </p>
      </div>

      {/* ── Search bar ── */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 16, marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        {/* Procedure */}
        <div style={{ flex: 2, minWidth: 200, position: "relative" }}>
          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Procedure</div>
          <input
            value={cptLabel || cpt}
            onChange={e => {
              const v = e.target.value; setCptLabel(v); const code = termMap[v] || termMap[v.toLowerCase()] || v; setCpt(code);
              if (v.length >= 2) { console.log("searchTerms count:", searchTerms.length, "termMap keys:", Object.keys(termMap).length); const m = searchTerms.filter(s => s.toLowerCase().includes(v.toLowerCase())).slice(0, 8); setProcSugs(m); setShowProcSug(m.length > 0); }
              else { setProcSugs([]); setShowProcSug(false); }
            }}
            onBlur={() => setTimeout(() => setShowProcSug(false), 150)}
            placeholder="Search procedure (e.g. Knee MRI)"
            style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }}
          />
          {showProcSug && procSugs.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 9, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden", maxHeight: 240, overflowY: "auto" }}>
              {procSugs.map((s, i) => (
                <div key={s} onMouseDown={() => { setCptLabel(s); const c = termMap[s] || termMap[s.toLowerCase()] || s; setCpt(c); setProcSugs([]); setShowProcSug(false); }}
                  style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, borderBottom: i < procSugs.length-1 ? "1px solid #f3f4f6" : "none" }}>
                  🔍 {s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location */}
        <div style={{ flex: 1.5, minWidth: 160, position: "relative" }}>
          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Market</div>
          <input
            value={locationLabel || location}
            onChange={e => {
              const v = e.target.value; setLocation(v); setLocationLabel("");
              if (v.length >= 2 && !isZip(v)) { const m = cityList.filter(c => c.toLowerCase().startsWith(v.toLowerCase())).slice(0, 6); setCitySugs(m); setShowCitySug(m.length > 0); }
              else { setCitySugs([]); setShowCitySug(false); }
            }}
            onBlur={() => setTimeout(() => setShowCitySug(false), 150)}
            placeholder="City or ZIP (e.g. Chicago)"
            style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }}
          />
          {showCitySug && citySugs.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 9, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden" }}>
              {citySugs.map((city, i) => (
                <div key={city} onMouseDown={() => { setLocation(city); setLocationLabel(city); setCitySugs([]); setShowCitySug(false); }}
                  style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, borderBottom: i < citySugs.length-1 ? "1px solid #f3f4f6" : "none" }}>
                  📍 {city}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Radius */}
        <div style={{ minWidth: 100 }}>
          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Radius</div>
          <select value={radius} onChange={e => setRadius(Number(e.target.value))}
            style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "10px 8px", fontSize: 14, background: "#fff" }}>
            {[5,10,15,20,30,50].map(r => <option key={r} value={r}>{r} mi</option>)}
          </select>
        </div>

        {/* Search button */}
        <button onClick={runSearch} disabled={running || !location.trim() || !cpt.trim()}
          style={{ padding: "10px 24px", background: running || !location.trim() || !cpt.trim() ? "#93c5fd" : "#2563eb", color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
          {running ? "Loading…" : "Analyze Market"}
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", color: "#b91c1c", fontSize: 13, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Results ── */}
      {data && (
        <>
          {/* Summary bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              {data.cpt && <span style={{ color: "#6b7280", fontWeight: 400 }}>{cptLabel || data.cpt} · </span>}
              {radius}mi from {locationLabel || location}
            </span>
            <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>
              {results.length} providers
            </span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              Payers: {payers.map(p => PAYER_LABELS[p] || p.toUpperCase()).join(" · ")}
            </span>
          </div>

          {/* Viewing as selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Viewing as:</span>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ key: "none", label: "Regional/New payer" }, ...payers.map(p => ({ key: p, label: PAYER_LABELS[p] || p.toUpperCase() }))].map(({ key, label }) => (
                <button key={key} onClick={() => setViewAs(key)}
                  style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20,
                    border: `1.5px solid ${viewAs === key ? "#d97706" : "#e5e7eb"}`,
                    background: viewAs === key ? "#fef3c7" : "#fff",
                    color: viewAs === key ? "#92400e" : "#6b7280",
                    cursor: "pointer", fontWeight: viewAs === key ? 700 : 400 }}>
                  {label}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 11, color: "#92400e", marginLeft: 4 }}>
              {viewAs === "none" ? "Showing market rates as negotiation targets" : `Your rate vs market — opportunities highlighted`}
            </span>
          </div>

          {/* Category filter */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { key: "all",        label: `All (${catCounts.all || 0})` },
              { key: "hospital",   label: `Hospitals (${catCounts.hospital || 0})` },
              { key: "individual", label: `Providers (${catCounts.individual || 0})` },
              { key: "ancillary",  label: `Labs & Imaging (${catCounts.ancillary || 0})` },
              { key: "behavioral", label: `Behavioral (${catCounts.behavioral || 0})` },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setFilterCat(key)}
                style={{ fontSize: 12, padding: "4px 12px", borderRadius: 20, border: `1.5px solid ${filterCat === key ? "#2563eb" : "#e5e7eb"}`, background: filterCat === key ? "#eff6ff" : "#fff", color: filterCat === key ? "#2563eb" : "#6b7280", cursor: "pointer", fontWeight: filterCat === key ? 700 : 400 }}>
                {label}
              </button>
            ))}
          </div>

          {/* Rate table */}
          {results.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 14 }}>
              No providers found for this filter.
            </div>
          ) : (
            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh", borderRadius: 12, border: "1px solid #e5e7eb" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#374151", minWidth: 220 }}>Provider</th>
                    {payers.map(payer => {
                      const pc = PAYER_COLORS[payer] || { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" };
                      const sorted = sortCol?.payer === payer;
                      return (
                        <th key={payer} onClick={() => toggleSort(payer)}
                          style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: pc.text, cursor: "pointer", background: pc.bg, minWidth: 130, userSelect: "none" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                            {PAYER_LABELS[payer] || payer.toUpperCase()}
                            {viewAs === payer && <span style={{ fontSize: 9, background: "#fef3c7", color: "#92400e", padding: "1px 4px", borderRadius: 4, marginLeft: 3 }}>YOUR RATE</span>}
                            <span style={{ fontSize: 10, opacity: 0.7 }}>
                              {sorted ? (sortCol.dir === "asc" ? "▲" : "▼") : "⇅"}
                            </span>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#374151", minWidth: 100 }}>Market low</th>
                    {viewAs !== "none" && <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#b91c1c", minWidth: 120, background: "#fff7f7" }}>Opportunity</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => {
                    // Compute market min across all payers for this provider
                    const allMins = payers.flatMap(p => (r.rates[p] || []).map(x => x.min)).filter(Boolean);
                    const marketLow = allMins.length ? Math.min(...allMins) : null;

                    return (
                      <tr key={r.npi}
                        onClick={() => fetchProviderIntel(r.npi, data.state)}
                        style={{ borderBottom: "1px solid #f3f4f6", background: selectedNpi === r.npi ? "#eff6ff" : i % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {r.is_hospital && <span style={{ fontSize: 12 }}>🏥</span>}
                            <div>
                              <div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>{r.name || r.npi}</div>
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.city}{r.city && r.address ? " · " : ""}{r.address?.slice(0, 30)}</div>
                            </div>
                          </div>
                        </td>
                        {payers.map(payer => {
                          const rates = r.rates[payer] || [];
                          const profRates = rates.filter(x => x.billing_class === "professional");
                          const instRates = rates.filter(x => x.billing_class === "institutional");
                          const payerMins = payerMinRates[payer] || [];
                          const profMin = profRates.length ? Math.min(...profRates.map(x => x.min)) : null;

                          return (
                            <td key={payer} style={{ padding: "10px 14px", textAlign: "center", verticalAlign: "top" }}>
                              {rates.length === 0 ? (
                                <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                              ) : (
                                <div>
                                  {profRates.length > 0 && (
                                    <div style={{ marginBottom: instRates.length ? 3 : 0 }}>
                                      <div style={{ fontWeight: 700, color: "#111827" }}>
                                        {fmtRange(Math.min(...profRates.map(x=>x.min)), Math.max(...profRates.map(x=>x.max)))}
                                      </div>
                                      <div style={{ fontSize: 10, color: "#9ca3af" }}>physician</div>
                                    </div>
                                  )}
                                  {instRates.length > 0 && (
                                    <div>
                                      <div style={{ fontWeight: 700, color: "#1d4ed8" }}>
                                        {fmtRange(Math.min(...instRates.map(x=>x.min)), Math.max(...instRates.map(x=>x.max)))}
                                      </div>
                                      <div style={{ fontSize: 10, color: "#9ca3af" }}>facility</div>
                                    </div>
                                  )}
                                  {profMin != null && <PositionBadge rate={profMin} allRates={payerMins} />}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td style={{ padding: "10px 14px", textAlign: "center" }}>
                          {marketLow != null ? (
                            <span style={{ fontWeight: 700, color: "#15803d" }}>{fmt(marketLow)}</span>
                          ) : <span style={{ color: "#d1d5db" }}>—</span>}
                        </td>
                      {viewAs !== "none" && (() => {
                        const yourRates = r.rates[viewAs] || [];
                        const yourMin = yourRates.length ? Math.min(...yourRates.map(x => x.min)) : null;
                        const saving = yourMin != null && marketLow != null ? yourMin - marketLow : null;
                        return (
                          <td style={{ padding: "10px 14px", textAlign: "center", background: "#fff7f7" }}>
                            {saving == null ? <span style={{ color: "#d1d5db" }}>—</span>
                             : saving <= 0 ? <span style={{ color: "#15803d", fontWeight: 700, fontSize: 12 }}>✓ Best rate</span>
                             : (
                              <div>
                                <div style={{ fontWeight: 700, color: "#b91c1c", fontSize: 13 }}>Save {fmt(saving)}</div>
                                <div style={{ fontSize: 10, color: "#9ca3af" }}>per procedure</div>
                              </div>
                            )}
                          </td>
                        );
                      })()}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Provider Intelligence drill-down */}
          {selectedNpi && (
            <div style={{ marginTop: 16, background: "#fff", border: "2px solid #2563eb", borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Provider Intelligence</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                    {providerData?.provider?.name || selectedNpi}
                  </div>
                  {providerData && <div style={{ fontSize: 12, color: "#6b7280" }}>{providerData.provider.city} · {providerData.peer_count} peer providers in {radius}mi · CPT {cpt}</div>}
                </div>
                <button onClick={() => { setSelectedNpi(null); setProviderData(null); }}
                  style={{ fontSize: 13, color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 12px", cursor: "pointer" }}>✕ Close</button>
              </div>
              {provLoading && <div style={{ textAlign: "center", padding: 24, color: "#6b7280" }}>Loading peer comparison…</div>}
              {providerData && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>
                      <th style={{ padding: "8px 14px", textAlign: "left", fontWeight: 700, color: "#374151" }}>Payer</th>
                      <th style={{ padding: "8px 14px", textAlign: "center", fontWeight: 700, color: "#374151" }}>Your Rate</th>
                      <th style={{ padding: "8px 14px", textAlign: "center", fontWeight: 700, color: "#374151" }}>Peer Avg</th>
                      <th style={{ padding: "8px 14px", textAlign: "center", fontWeight: 700, color: "#374151" }}>Peer Max</th>
                      <th style={{ padding: "8px 14px", textAlign: "center", fontWeight: 700, color: "#15803d" }}>Opportunity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providerData.opportunities.map((o, i) => {
                      const pc = PAYER_COLORS[o.payer] || { bg: "#f3f4f6", text: "#374151" };
                      const opp = o.opportunity;
                      return (
                        <tr key={o.payer} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ fontWeight: 700, color: pc.text }}>{PAYER_LABELS[o.payer] || o.payer.toUpperCase()}</span>
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700 }}>
                            {o.your_min ? fmt(o.your_min) : <span style={{ color: "#d1d5db" }}>Not contracted</span>}
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "center", color: "#374151" }}>
                            {o.peer_avg ? fmt(o.peer_avg) : "—"}
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "center", color: "#374151" }}>
                            {o.peer_max ? fmt(o.peer_max) : "—"}
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            {opp == null ? <span style={{ color: "#d1d5db" }}>—</span>
                             : opp <= 0 ? <span style={{ color: "#6b7280", fontSize: 12 }}>At or above avg</span>
                             : <div>
                                <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14 }}>+{fmt(opp)}</div>
                                <div style={{ fontSize: 10, color: "#9ca3af" }}>negotiate up</div>
                              </div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Footer note */}
          <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 12, lineHeight: 1.5 }}>
            Rates from federal Transparency in Coverage machine-readable files. Published negotiated rates between payers and providers. Not valid for cash-pay or uninsured patients.
            {" "}Data is public under 45 CFR § 180.
          </p>

          <div style={{ marginTop: 24, background: "#0f172a", borderRadius: 12, padding: "20px 24px", color: "#e2e8f0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f8fafc" }}>API Access</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>Integrate MioPrice rate intelligence directly into your systems</div>
              </div>

            </div>
            <div style={{ fontFamily: "monospace", fontSize: 12, background: "#1e293b", borderRadius: 8, padding: "14px 16px", overflowX: "auto", marginBottom: 12, lineHeight: 1.8 }}>
              <div style={{ color: "#94a3b8", marginBottom: 4 }}># Cross-payer rate matrix for any procedure + market</div>
              <div><span style={{ color: "#86efac" }}>GET </span><span style={{ color: "#e2e8f0" }}>https://api.mioprice.com/api/payer-intelligence</span></div>
              <div style={{ color: "#94a3b8" }}>{"  "}?cpt=<span style={{ color: "#fbbf24" }}>73721</span><span style={{ color: "#475569" }}>          # CPT code</span></div>
              <div style={{ color: "#94a3b8" }}>{"  "}&amp;lat=<span style={{ color: "#fbbf24" }}>41.8755</span>&amp;lng=<span style={{ color: "#fbbf24" }}>-87.6244</span><span style={{ color: "#475569" }}>   # market coordinates</span></div>
              <div style={{ color: "#94a3b8" }}>{"  "}&amp;radius=<span style={{ color: "#fbbf24" }}>15</span><span style={{ color: "#475569" }}>               # miles</span></div>
              <div style={{ color: "#94a3b8" }}>{"  "}&amp;api_key=<span style={{ color: "#fbbf24" }}>YOUR_KEY</span><span style={{ color: "#475569" }}>         # contact sales</span></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {[["737 CPTs", "All shoppable procedures covered"], ["IL + CA", "More states Q3 2026"], ["JSON / REST", "No SDK needed"]].map(([title, desc]) => (
                <div key={title} style={{ background: "#1e293b", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#f8fafc", marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Empty state ── */}
      {!data && !running && !error && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Cross-payer rate benchmarking</div>
          <div style={{ fontSize: 13, maxWidth: 420, margin: "0 auto" }}>
            Select a procedure and market to see negotiated rates across all payers at every provider — side by side.
          </div>
        </div>
      )}
    </div>
  );
}
